import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";

let initialized = false;
const isDev = import.meta.env.DEV;

/**
 * Initialize the Amplitude client SDK. Safe to call multiple times.
 * No-op when running on server or when AMPLITUDE_API_KEY is not in window.ENV.
 */
export function initAmplitude(): void {
  if (typeof window === "undefined") return;
  const ENV = (window as Window & { ENV?: { AMPLITUDE_API_KEY?: string } }).ENV;
  const apiKey = ENV?.AMPLITUDE_API_KEY?.trim();
  if (!apiKey || initialized) return;
  amplitude.init(apiKey);
  initialized = true;
}

/**
 * Track an event from the client. No-op when SDK is not initialized
 * (e.g. no API key, or running on server).
 */
export function ampliClientTrack(
  eventName: string,
  eventProperties?: Record<string, unknown>,
  userId?: string | null
): void {
  if (!initialized) return;
  amplitude.track(eventName, eventProperties ?? undefined, {
    ...(userId && { user_id: userId }),
  });
  if (isDev) {
    console.log(
      "[amplitude track]",
      eventName,
      eventProperties ?? {},
      userId ?? undefined
    );
  }
}

/**
 * Set the current user id for subsequent client-side events.
 * Call after login so client tracks are associated with the user.
 */
export function setUserId(userId: string | null | undefined): void {
  if (!initialized) return;
  amplitude.setUserId(userId ?? undefined);
  if (isDev) {
    console.log("[amplitude setUserId]", userId ?? undefined);
  }
}

/**
 * Set user properties (e.g. name) for the current user in Amplitude.
 * Call after login with user profile; pass name: undefined to clear on logout.
 */
export function setUserProperties(properties: { name?: string | null }): void {
  if (!initialized) return;
  const { name } = properties;
  const identify = new Identify();
  if (name != null && name.trim() !== "") {
    identify.set("name", name);
    amplitude.identify(identify);
  }
  if (isDev) {
    console.log("[amplitude setUserProperties]", { name: name ?? undefined });
  }
}

export default amplitude;
